import type { CSSProperties, FC } from 'react';
import { Avatar, Popover, Skeleton } from 'antd';
import { TbBuilding, TbBuildingBank, TbExternalLink, TbWorld } from 'react-icons/tb';
import { Link } from 'react-router-dom';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { normalizeLogoDevDomain } from '@/queries';
import {
    CompactHoverCardIdentity,
    CompactHoverCardMetadataRow,
    CompactHoverCardSection,
    CompactHoverCardSurface,
} from '@/shared/components/cards';
import TickerExchangeTag from '@/shared/components/tags/TickerExchangeTag';
import { ellipsisStyling, resolveCorporateLogo } from '@/utilities';

import EnrichedPill from '../EnrichedPill';

export interface EnrichedCompanyPillProps {
    assetType?: 'company' | 'fund';
    avatarSize?: number;
    companyName: string;
    exchange?: string;
    imageUrl?: string;
    loading?: boolean;
    style?: CSSProperties;
    symbol: string;
    website?: string;
}

/**
 * Public-market identity used by dense lists and rich references. Logo.dev's
 * image endpoint is the canonical source on every plan; legacy vendor images
 * are deliberately not used as avatar fallbacks.
 */
const EnrichedCompanyPill: FC<EnrichedCompanyPillProps> = ({
    assetType = 'company',
    avatarSize = 24,
    companyName,
    exchange,
    imageUrl,
    loading = false,
    style,
    symbol,
    website,
}) => {
    const { token } = useAntDesignTheme();
    const href = `/search/${assetType === 'fund' ? 'funds' : 'companies'}/${encodeURIComponent(symbol)}/overview`;
    const resolvedLogo = resolveCorporateLogo(website, imageUrl, {}, { name: companyName, ticker: symbol });
    const avatar =
        loading && !resolvedLogo ? (
            <Skeleton.Avatar active shape='square' size={avatarSize} />
        ) : (
            <Avatar
                icon={<TbBuilding />}
                shape='square'
                size={avatarSize}
                src={resolvedLogo}
                style={{
                    background: token.colorBgContainer,
                    border: `1px solid ${token.colorBorder}`,
                    flex: 'none',
                }}
            />
        );

    const preview = (
        <CompactHoverCardSurface style={{ marginTop: '0.375rem' }} width='22rem'>
            <CompactHoverCardSection divider={false}>
                <CompactHoverCardIdentity
                    avatar={avatar}
                    subtitle={
                        <TickerExchangeTag
                            assetType={assetType}
                            companyName={companyName}
                            compact
                            exchange={exchange}
                            symbol={symbol}
                            website={website}
                        />
                    }
                    title={companyName}
                />
            </CompactHoverCardSection>
            <CompactHoverCardSection>
                {exchange ? (
                    <CompactHoverCardMetadataRow icon={<TbBuildingBank />}>{exchange}</CompactHoverCardMetadataRow>
                ) : null}
                {website ? (
                    <CompactHoverCardMetadataRow icon={<TbWorld />}>
                        <a href={website} rel='noopener noreferrer' target='_blank'>
                            {normalizeLogoDevDomain(website) || website}
                        </a>
                    </CompactHoverCardMetadataRow>
                ) : null}
            </CompactHoverCardSection>
            <CompactHoverCardSection>
                <Link to={href} style={{ alignItems: 'center', display: 'inline-flex', gap: '0.375rem' }}>
                    <TbExternalLink />
                    Open {assetType === 'fund' ? 'Fund' : 'Company'}
                </Link>
            </CompactHoverCardSection>
        </CompactHoverCardSurface>
    );

    return (
        <Popover arrow={false} content={preview} mouseEnterDelay={0.25} placement='bottomLeft' trigger='hover'>
            <span style={{ display: 'inline-flex', maxWidth: '100%' }}>
                <EnrichedPill interactive={false} style={{ margin: 0, maxWidth: '100%', ...style }}>
                    {avatar}
                    <Link to={href} style={{ ...ellipsisStyling, color: 'inherit', flex: '1 1 auto', minWidth: 0 }}>
                        {companyName}
                    </Link>
                    <TickerExchangeTag
                        assetType={assetType}
                        companyName={companyName}
                        compact
                        exchange={exchange}
                        styles={{ flex: '0 0 auto', margin: 0 }}
                        symbol={symbol}
                        website={website}
                    />
                </EnrichedPill>
            </span>
        </Popover>
    );
};

export default EnrichedCompanyPill;
